/**
 * @fileoverview Inline chat diagram card. Renders a DiagramSpec (Mermaid) and
 * offers "Save to canvas" (when a save handler is supplied), copy-source, and
 * PNG / SVG export. Mirrors ChartCard.
 */
import { useState } from "react";
import { BookmarkPlus, Check, Copy, Download } from "lucide-react";
import type { DiagramSpec } from "@cvx/lib/diagrams/spec";
import { toast } from "sonner";
import { DiagramRenderer } from "../diagramRenderer";
import {
  diagramFileStem,
  downloadMermaidPng,
  downloadMermaidSvgFile,
} from "../mermaid";

const actionButtonClass =
  "inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20 disabled:opacity-60";

export function DiagramActions({
  spec,
  onSaveToCanvas,
}: {
  spec: DiagramSpec;
  onSaveToCanvas?: (spec: DiagramSpec) => Promise<void> | void;
}) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const runExport = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch {
      toast.error("The diagram could not be exported — its syntax is invalid");
    }
  };

  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        title="Copy Mermaid source"
        onClick={async () => {
          await navigator.clipboard.writeText(spec.code);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className={actionButtonClass}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <button
        type="button"
        title="Download as PNG"
        onClick={() =>
          runExport(() =>
            downloadMermaidPng(spec.code, diagramFileStem(spec.title)),
          )
        }
        className={actionButtonClass}
      >
        <Download className="h-3 w-3" />
        PNG
      </button>
      <button
        type="button"
        title="Download as SVG"
        onClick={() =>
          runExport(() =>
            downloadMermaidSvgFile(spec.code, diagramFileStem(spec.title)),
          )
        }
        className={actionButtonClass}
      >
        <Download className="h-3 w-3" />
        SVG
      </button>
      {onSaveToCanvas && (
        <button
          type="button"
          disabled={saved}
          onClick={async () => {
            try {
              await onSaveToCanvas(spec);
              setSaved(true);
            } catch {
              // The save handler surfaces its own error toast.
            }
          }}
          className={actionButtonClass}
        >
          {saved ? (
            <Check className="h-3 w-3" />
          ) : (
            <BookmarkPlus className="h-3 w-3" />
          )}
          {saved ? "Saved" : "Save to canvas"}
        </button>
      )}
    </div>
  );
}

export function DiagramCard({
  spec,
  onSaveToCanvas,
}: {
  spec: DiagramSpec;
  onSaveToCanvas?: (spec: DiagramSpec) => Promise<void> | void;
}) {
  return (
    <div className="not-prose my-3 rounded-lg border border-border bg-background p-3 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{spec.title}</p>
          {spec.description && (
            <p className="text-xs text-muted-foreground">{spec.description}</p>
          )}
        </div>
        <DiagramActions spec={spec} onSaveToCanvas={onSaveToCanvas} />
      </div>
      <DiagramRenderer code={spec.code} title={spec.title} />
    </div>
  );
}
